import React, { useState } from 'react';
import { Box, Typography, LinearProgress, Tooltip, useTheme, Accordion, AccordionSummary, AccordionDetails, useMediaQuery, ClickAwayListener } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

interface ProgressProps {
  data: {
    dreamCount: {
      current: number;
      medals: {
        threshold: number;
        name: string;
        achieved: boolean;
        color: string;
      }[];
    };
    categoryCount: {
      current: number;
      medals: {
        threshold: number;
        name: string;
        achieved: boolean;
        color: string;
      }[];
    };
  };
  type: 'dreams' | 'categories';
}

const Progress: React.FC<ProgressProps> = ({ data, type }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const isMobile = useMediaQuery('(max-width:620px)');
  const { t } = useTranslation();
  // Utiliser un identifiant unique basé sur le type pour éviter les conflits entre les instances
  const tooltipPrefix = type === 'dreams' ? 'dream_' : 'category_';
  const [openTooltips, setOpenTooltips] = useState<{[key: string]: boolean}>({});

  const getNextThreshold = (current: number, medals: { threshold: number }[]) => {
    const next = medals.find(m => m.threshold > current);
    return next ? next.threshold : medals[medals.length - 1].threshold;
  };

  const calculateProgress = (current: number, medals: { threshold: number }[]) => {
    const nextThreshold = getNextThreshold(current, medals);
    const prevThreshold = medals.filter(m => m.threshold <= current).slice(-1)[0]?.threshold || 0;
    return ((current - prevThreshold) / (nextThreshold - prevThreshold)) * 100;
  };

  const getMedalName = (medal: any) => {
    const medalMap: { [key: string]: string } = {
      'Premier Pas': 'premier_pas',
      'Voyageur': 'voyageur',
      'Explorateur': 'explorateur',
      'Maître': 'maitre',
      'Légende': 'legende',
      'Curieux': 'curieux',
      'Aventurier': 'aventurier',
      'Erudit': 'erudit',
      'Sage': 'sage',
      'Oracle': 'oracle'
    };
    
    const key = medalMap[medal.name] || medal.name.toLowerCase();
    return t(`statistics.progress.medals.${key}.name`);
  };

  const getMedalTooltip = (medal: any, type: string) => {
    if (type === 'dreams') {
      return `${medal.achieved ? t('statistics.progress.medals.obtained') : t('statistics.progress.medals.toObtain')} : ${medal.threshold} ${t('statistics.progress.medals.dreamsCount')}`;
    } else {
      const medalMap: { [key: string]: string } = {
        'Premier Pas': 'premier_pas',
        'Voyageur': 'voyageur',
        'Explorateur': 'explorateur',
        'Maître': 'maitre',
        'Légende': 'legende',
        'Curieux': 'curieux',
        'Aventurier': 'aventurier',
        'Erudit': 'erudit',
        'Sage': 'sage',
        'Oracle': 'oracle'
      };
      
      const key = medalMap[medal.name] || medal.name.toLowerCase();
      return `${medal.achieved ? t('statistics.progress.medals.obtained') : t('statistics.progress.medals.toObtain')} : ${t(`statistics.progress.medals.${key}.description`)}`;
    }
  };

  const handleTooltipToggle = (index: number) => {
    const tooltipKey = `${tooltipPrefix}${index}`;
    setOpenTooltips(prev => ({
      ...prev,
      [tooltipKey]: !prev[tooltipKey]
    }));
  };

  const handleTooltipClose = (index: number) => {
    const tooltipKey = `${tooltipPrefix}${index}`;
    setOpenTooltips(prev => ({
      ...prev,
      [tooltipKey]: false
    }));
  };
  
  const isTooltipOpen = (index: number) => {
    const tooltipKey = `${tooltipPrefix}${index}`;
    return Boolean(openTooltips[tooltipKey]);
  };

  const renderMedal = (medal: any, index: number, isLastAchieved: boolean = false) => {
    const shadowConfig = type === 'dreams' 
      ? {
          normal: 'drop-shadow(0 8px 18px rgba(0, 0, 0, 0.5))',
          hover: 'drop-shadow(0 12px 8px rgba(0, 0, 0, 0.5))'
        }
      : {
          normal: 'drop-shadow(0 8px 18px rgba(0, 0, 0, 0.9))',
          hover: 'drop-shadow(0 12px 24px rgba(255, 75, 140, 0.6))'
        };

    const titleStyle = type === 'dreams'
      ? {
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(30px)',
        }
      : {
          background: 'rgba(155, 255, 255, 0.4)',
        };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            ...titleStyle,
            color: 'black',
            px: 2,
            py: 0.5,
            borderRadius: 5,
            fontSize: '0.875rem',
            fontWeight: 'medium',
          }}
        >
          {getMedalName(medal)}
        </Box>
        <Box
          className="badge-container"
          sx={{
            position: 'relative',
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: medal.achieved ? 1 : 0.03,
            filter: medal.achieved 
              ? `brightness(1) ${shadowConfig.normal}`
              : 'brightness(0.7)',
            transition: 'all 0.3s ease',
            '&:hover': medal.achieved ? {
              filter: `brightness(1.3) ${shadowConfig.hover}`,
              transform: type === 'dreams' ? 'none' : 'translateY(-2px) scale(2.1)',
              transformOrigin: 'center center',
              zIndex: 1000,
            } : {
              filter: 'brightness(0.7)',
            }
          }}
        >
          <img 
            src={`/assets/badges/badge-${type === 'dreams' ? 'dream' : 'category'}-${index + 1}.png`}
            alt={getMedalName(medal)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Box>
    );
  };

  const medals = type === 'dreams' ? data.dreamCount.medals : data.categoryCount.medals;
  const current = type === 'dreams' ? data.dreamCount.current : data.categoryCount.current;
  const lastAchievedIndex = medals.map(m => m.achieved).lastIndexOf(true);

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: 5,
      width: '100%',
      p: 1
    }}>
      <Box>
        {isMobile ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              {lastAchievedIndex >= 0 && (
                <Tooltip 
                  open={isTooltipOpen(lastAchievedIndex)}
                  onClose={() => handleTooltipClose(lastAchievedIndex)}
                  title={getMedalTooltip(medals[lastAchievedIndex], type)}
                  placement="top"
                  arrow
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                    '& .MuiTooltip-tooltip': {
                      fontSize: '0.875rem',
                      padding: '8px 12px',
                      borderRadius: '4px'
                    }
                  }}
                >
                  <div onClick={() => handleTooltipToggle(lastAchievedIndex)} style={{ display: 'inline-block' }}>
                    {renderMedal(medals[lastAchievedIndex], lastAchievedIndex, true)}
                  </div>
                </Tooltip>
              )}
            </Box>

            <Accordion 
              expanded={expanded} 
              onChange={() => setExpanded(!expanded)}
              sx={{
                background: 'transparent',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                mt: 2
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '10px',
                  '& .MuiAccordionSummary-content': {
                    justifyContent: 'center'
                  }
                }}
              >
                <Typography sx={{ color: 'white' }}>
                  {t('statistics.progress.medalTable')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ background: 'transparent' }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 3,
                  alignItems: 'center',
                  mt: 2,
                  mb: 2
                }}>
                  {medals.map((medal, index) => {
                    // Utiliser un index modifié pour les médailles dans l'accordéon
                    const accordionIndex = index + 100; // Ajouter un offset pour éviter les conflits
                    return (
                      <Tooltip 
                        key={index}
                        open={isTooltipOpen(accordionIndex)}
                        onClose={() => handleTooltipClose(accordionIndex)}
                        title={getMedalTooltip(medal, type)}
                        placement="top"
                        arrow
                        sx={{
                          bgcolor: 'rgba(0, 0, 0, 0.8)',
                          '& .MuiTooltip-tooltip': {
                            fontSize: '0.875rem',
                            padding: '8px 12px',
                            borderRadius: '4px'
                          }
                        }}
                      >
                        <div onClick={() => handleTooltipToggle(accordionIndex)} style={{ display: 'inline-block' }}>
                          {renderMedal(medal, index)}
                        </div>
                      </Tooltip>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            {medals.map((medal, index) => (
              <Tooltip 
                key={index}
                title={getMedalTooltip(medal, type)}
                placement="top"
                arrow
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  '& .MuiTooltip-tooltip': {
                    fontSize: '0.875rem',
                    padding: '8px 12px',
                    borderRadius: '4px'
                  }
                }}
              >
                <div style={{ display: 'inline-block' }}>
                  {renderMedal(medal, index)}
                </div>
              </Tooltip>
            ))}
          </Box>
        )}

        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          width: '100%',
          mt: 6
        }}>
          <Typography variant="body2" sx={{ color: '#68f1d1', mb: 1 }}>
            {type === 'dreams' 
              ? t('statistics.progress.nextBadgeProgressDreams')
              : t('statistics.progress.nextBadgeProgress')}
          </Typography>

          <LinearProgress 
            variant="determinate" 
            value={Math.min((current / getNextThreshold(current, medals)) * 100, 100)} 
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': {
                background: type === 'dreams' 
                  ? 'linear-gradient(to right, #68f1d1 70%, #FF4B8C 100%)'
                  : 'linear-gradient(to right, #FF4B8C 70%, #2E3192)',
                borderRadius: 5,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Progress;
